import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type Listino,
  type OpzioniListino,
  useCreaListino,
  useSalvaTestataListino,
} from '../api/listini.js';
import { Modale } from '../componenti/Modale.js';
import { giornoItaliano } from '../giorni.js';
import { t } from '../i18n/lingua.js';

/**
 * Il modulo di un listino: gli stessi campi per crearlo e per modificarlo.
 *
 * Il v2 ha una maschera sola per «Nuovo» e per «Modifica», e ha ragione: i
 * campi sono gli stessi, e averne due significherebbe due posti dove
 * sbagliare la stessa etichetta.
 *
 * Solo il nome è obbligatorio. Cliente, date e resa si possono riempire dopo:
 * costringere a decidere tutto subito fa abbandonare la maschera a metà, e
 * un listino senza cliente è un listino che si sta ancora preparando.
 */
export function ModaleListino({
  listino,
  opzioni,
  onChiudi,
  onCreato,
}: {
  /** Nullo per un listino nuovo. */
  listino: Listino | null;
  opzioni: OpzioniListino | undefined;
  onChiudi: () => void;
  onCreato?: (id: number) => void;
}) {
  const crea = useCreaListino();
  const salva = useSalvaTestataListino(listino?.id ?? 0);

  const [campi, setCampi] = useState({
    name: listino?.name ?? '',
    customerId: listino?.customerId ? String(listino.customerId) : '',
    countryId: listino?.countryId ? String(listino.countryId) : '',
    shippingMethod: listino?.shippingMethod ?? 'exw',
    startDate: giornoItaliano(listino?.startDate),
    endDate: giornoItaliano(listino?.endDate),
    flatDiscountPercentage: listino?.flatDiscountPercentage ?? '',
    notes: listino?.notes ?? '',
  });

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const inCorso = crea.isPending || salva.isPending;
  const errore =
    crea.error instanceof ErroreApi
      ? crea.error.message
      : salva.error instanceof ErroreApi
        ? salva.error.message
        : null;

  const conferma = () => {
    const corpo = {
      name: campi.name.trim(),
      customerId: campi.customerId ? Number(campi.customerId) : null,
      countryId: campi.countryId ? Number(campi.countryId) : null,
      shippingMethod: campi.shippingMethod,
      startDate: campi.startDate || null,
      endDate: campi.endDate || null,
      flatDiscountPercentage:
        campi.flatDiscountPercentage === '' ? null : Number(campi.flatDiscountPercentage),
      notes: campi.notes.trim() || null,
    };

    if (listino) salva.mutate(corpo, { onSuccess: onChiudi });
    else crea.mutate(corpo, { onSuccess: ({ id }) => onCreato?.(id) });
  };

  return (
    <Modale
      titolo={listino ? t('Modifica il listino') : 'Nuovo listino'}
      nota={
        listino
          ? undefined
          : t('Gli articoli si aggiungono dopo, dalla scheda. Se il listino esiste già per il periodo precedente, aprilo e usa Duplica: si porta dietro tutti i prezzi.')
      }
      larga
      inCorso={inCorso}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={inCorso}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={inCorso || campi.name.trim().length === 0}
          >
            {inCorso ? 'Salvo…' : listino ? 'Salva' : 'Crea'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}
      <div className="fields">
        <div className="field span-2">
          <label htmlFor="l-nome">
            {t('Nome del listino')}<span className="req">*</span>
          </label>
          <input
            id="l-nome"
            className="input"
            value={campi.name}
            autoFocus
            onChange={(e) => cambia('name', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="l-cliente">{t('Cliente')}</label>
          <select
            id="l-cliente"
            className="select"
            value={campi.customerId}
            onChange={(e) => cambia('customerId', e.target.value)}
          >
            <option value="">{t('Nessuno')}</option>
            {opzioni?.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          {/* un listino può valere per un intero paese invece che per un
              cliente: nei dati storici non succede mai, ma il v2 lo permette */}
          <label htmlFor="l-paese">
            {t('Nazione')}<span className="dim" style={{ fontWeight: 400 }}> {t('— se vale per tutto un paese')}</span>
          </label>
          <select
            id="l-paese"
            className="select"
            value={campi.countryId}
            onChange={(e) => cambia('countryId', e.target.value)}
          >
            <option value="">{t('Nessuna')}</option>
            {opzioni?.countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="l-resa">{t('Resa')}</label>
          <select
            id="l-resa"
            className="select"
            value={campi.shippingMethod}
            onChange={(e) => cambia('shippingMethod', e.target.value)}
          >
            {(opzioni?.shippingMethods ?? []).map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="l-sconto">{t('Sconto globale (%)')}</label>
          <input
            id="l-sconto"
            className="input num"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={campi.flatDiscountPercentage}
            onChange={(e) => cambia('flatDiscountPercentage', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="l-dal">{t('Valido dal')}</label>
          <input
            id="l-dal"
            className="input"
            type="date"
            value={campi.startDate}
            onChange={(e) => cambia('startDate', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="l-al">{t('Valido fino al')}</label>
          <input
            id="l-al"
            className="input"
            type="date"
            value={campi.endDate}
            onChange={(e) => cambia('endDate', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label htmlFor="l-note">{t('Annotazioni')}</label>
          <textarea
            id="l-note"
            className="textarea"
            rows={3}
            value={campi.notes}
            onChange={(e) => cambia('notes', e.target.value)}
          />
        </div>
      </div>
    </Modale>
  );
}
